import { z } from 'zod';

// src/constants/index.ts
var ClientStatus = {
  PROSPECT: "prospect",
  ACTIVE: "active",
  INACTIVE: "inactive",
  CHURNED: "churned",
  ARCHIVED: "archived"
};
var EmployeeRole = {
  ADMIN: "admin",
  MANAGER: "manager",
  DEVELOPER: "developer",
  DESIGNER: "designer",
  QA: "qa",
  DEVOPS: "devops",
  PM: "pm",
  OTHER: "other"
};
var EmployeeStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  ON_LEAVE: "on_leave"
};
var ProjectStatus = {
  PIPELINE: "pipeline",
  ACTIVE: "active",
  ON_HOLD: "on_hold",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  ARCHIVED: "archived"
};
var ProjectType = {
  CLIENT: "client",
  INTERNAL: "internal",
  RND: "rnd",
  MAINTENANCE: "maintenance"
};
var BillingType = {
  FIXED: "fixed",
  HOURLY: "hourly",
  RETAINER: "retainer"
};
var TaskStatus = {
  BACKLOG: "backlog",
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  REVIEW: "review",
  DONE: "done",
  CANCELLED: "cancelled"
};
var TaskType = {
  EPIC: "epic",
  STORY: "story",
  TASK: "task",
  BUG: "bug",
  SUBTASK: "subtask"
};
var TaskPriority = {
  CRITICAL: "critical",
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low"
};
var MilestoneStatus = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  MISSED: "missed"
};
var DocumentStatus = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived"
};
var InsightType = {
  PROJECT_HEALTH: "project_health",
  RESOURCE_ALERT: "resource_alert",
  BUDGET_WARNING: "budget_warning",
  PERFORMANCE: "performance",
  RECOMMENDATION: "recommendation"
};
var InsightSeverity = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "critical"
};
var MessageRole = {
  USER: "user",
  ASSISTANT: "assistant",
  SYSTEM: "system"
};
var APP_NAME = "Aelita";
var APP_VERSION = "0.1.0";
var COMPANY = {
  NAME: "Lyoko Tech",
  DOMAIN: "lyokotech.com",
  APP_DOMAIN: "aelita.lyokotech.com"
};
var PORTS = {
  API: 12310,
  WEB: 12320,
  POSTGRES: 12301,
  POSTGRES_TEST: 12302,
  REDIS: 12303
};
var PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100
};
var AUTH = {
  JWT_EXPIRES_IN: "7d",
  JWT_REFRESH_EXPIRES_IN: "30d",
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  SALT_ROUNDS: 12
};
var API_KEY = {
  PREFIX: "ak_",
  KEY_BYTE_LENGTH: 32,
  MAX_KEYS_PER_EMPLOYEE: 10,
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 100
};
var TIME_TRACKING = {
  MIN_DURATION_MINUTES: 1,
  MAX_DURATION_MINUTES: 24 * 60,
  // 24 hours
  DEFAULT_BILLABLE: true
};
var CLIENT_STATUS_LABELS = {
  [ClientStatus.PROSPECT]: "Prospect",
  [ClientStatus.ACTIVE]: "Active",
  [ClientStatus.INACTIVE]: "Inactive",
  [ClientStatus.CHURNED]: "Churned",
  [ClientStatus.ARCHIVED]: "Archived"
};
var EMPLOYEE_ROLE_LABELS = {
  [EmployeeRole.ADMIN]: "Administrator",
  [EmployeeRole.MANAGER]: "Manager",
  [EmployeeRole.DEVELOPER]: "Developer",
  [EmployeeRole.DESIGNER]: "Designer",
  [EmployeeRole.QA]: "QA Engineer",
  [EmployeeRole.DEVOPS]: "DevOps Engineer",
  [EmployeeRole.PM]: "Project Manager",
  [EmployeeRole.OTHER]: "Other"
};
var PROJECT_STATUS_LABELS = {
  [ProjectStatus.PIPELINE]: "Pipeline",
  [ProjectStatus.ACTIVE]: "Active",
  [ProjectStatus.ON_HOLD]: "On Hold",
  [ProjectStatus.COMPLETED]: "Completed",
  [ProjectStatus.CANCELLED]: "Cancelled",
  [ProjectStatus.ARCHIVED]: "Archived"
};
var TASK_STATUS_LABELS = {
  [TaskStatus.BACKLOG]: "Backlog",
  [TaskStatus.TODO]: "To Do",
  [TaskStatus.IN_PROGRESS]: "In Progress",
  [TaskStatus.REVIEW]: "In Review",
  [TaskStatus.DONE]: "Done",
  [TaskStatus.CANCELLED]: "Cancelled"
};
var TASK_PRIORITY_LABELS = {
  [TaskPriority.CRITICAL]: "Critical",
  [TaskPriority.HIGH]: "High",
  [TaskPriority.MEDIUM]: "Medium",
  [TaskPriority.LOW]: "Low"
};
var InvoiceStatus = {
  DRAFT: "draft",
  SENT: "sent",
  PAID: "paid",
  EXPIRED: "expired",
  VOID: "void"
};
var PaymentStatus = {
  PENDING: "pending",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  REFUNDED: "refunded"
};
var PaymentMethod = {
  CARD: "card",
  ACH: "ach",
  APPLE_PAY: "apple_pay",
  GOOGLE_PAY: "google_pay",
  OTHER: "other"
};
var INVOICE_NUMBER_REGEX = /^[0-9A-F]{6}$/;
var DEFAULT_PAYMENT_LINK_EXPIRY_DAYS = 30;
var INVOICE_NUMBER_MAX_VALUE = 16777215;

// src/utils/index.ts
function formatDateISO(date) {
  return date.toISOString().split("T")[0] ?? "";
}
function formatDate(date, locale = "en-US") {
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}
function formatDateTime(date, locale = "en-US") {
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function startOfDay(date) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}
function endOfDay(date) {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}
function startOfWeek(date) {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1);
  result.setDate(diff);
  result.setHours(0, 0, 0, 0);
  return result;
}
function endOfWeek(date) {
  const result = startOfWeek(date);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
}
function isPast(date) {
  return date < /* @__PURE__ */ new Date();
}
function isFuture(date) {
  return date > /* @__PURE__ */ new Date();
}
function diffInDays(date1, date2) {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1e3 * 60 * 60 * 24));
}
function formatDuration(minutes) {
  if (minutes < 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
function formatDurationDecimal(minutes) {
  return (minutes / 60).toFixed(2);
}
function parseDuration(input) {
  const trimmed = input.trim().toLowerCase();
  const hmMatch = trimmed.match(/^(?:(\d+)h)?\s*(?:(\d+)m)?$/);
  if (hmMatch?.[1] !== void 0 || hmMatch?.[2] !== void 0) {
    const hours = parseInt(hmMatch[1] ?? "0", 10);
    const mins = parseInt(hmMatch[2] ?? "0", 10);
    return hours * 60 + mins;
  }
  const decimalMatch = trimmed.match(/^(\d+(?:\.\d+)?)h$/);
  if (decimalMatch?.[1]) {
    return Math.round(parseFloat(decimalMatch[1]) * 60);
  }
  const colonMatch = trimmed.match(/^(\d+):(\d{2})$/);
  if (colonMatch?.[1] && colonMatch[2]) {
    const hours = parseInt(colonMatch[1], 10);
    const mins = parseInt(colonMatch[2], 10);
    return hours * 60 + mins;
  }
  const minsOnly = trimmed.match(/^(\d+)$/);
  if (minsOnly?.[1]) {
    return parseInt(minsOnly[1], 10);
  }
  return null;
}
function calculateDuration(start, end) {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (1e3 * 60)));
}
function formatInvoiceNumber(n) {
  if (!Number.isInteger(n) || n < 0 || n > INVOICE_NUMBER_MAX_VALUE) {
    throw new RangeError(
      `Invoice counter must be an integer between 0 and ${INVOICE_NUMBER_MAX_VALUE}, got ${n}`
    );
  }
  return n.toString(16).toUpperCase().padStart(6, "0");
}
function parseInvoiceNumber(s) {
  if (!INVOICE_NUMBER_REGEX.test(s)) {
    throw new TypeError(
      `Invalid invoice number "${s}": must be exactly 6 uppercase hex characters (0-9, A-F)`
    );
  }
  return parseInt(s, 16);
}
function centsToDisplay(cents) {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(dollars);
}
function slugify(text) {
  return text.toString().toLowerCase().trim().replace(/\s+/g, "-").replace(/[^\w-]+/g, "").replace(/--+/g, "-").replace(/^-+/, "").replace(/-+$/, "");
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function toTitleCase(text) {
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}
function getInitials(name, maxLength = 2) {
  return name.split(" ").map((part) => part[0]).filter(Boolean).slice(0, maxLength).join("").toUpperCase();
}
function formatFullName(firstName, lastName) {
  return `${firstName} ${lastName}`.trim();
}
function formatCurrency(amount, currency = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(amount);
}
function formatPercent(value, decimals = 0) {
  return `${(value * 100).toFixed(decimals)}%`;
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function roundTo(value, decimals) {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
function groupBy(array, keyFn) {
  return array.reduce(
    (result, item) => {
      const key = keyFn(item);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    },
    {}
  );
}
function uniqueBy(array, keyFn) {
  const seen = /* @__PURE__ */ new Set();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function sortBy(array, keyFn, desc = false) {
  const sorted = [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return desc ? sorted.reverse() : sorted;
}
function pick(obj, keys) {
  const result = {};
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}
function omit(obj, keys) {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}
function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
function isValidUuid(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function retry(fn, options = {}) {
  const { maxRetries = 3, baseDelay = 1e3 } = options;
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries - 1) {
        await sleep(baseDelay * Math.pow(2, attempt));
      }
    }
  }
  throw lastError;
}
var uuidSchema = z.string().uuid();
var requiredDateSchema = z.preprocess((val) => {
  if (val === null || val === void 0) return val;
  return val instanceof Date ? val : new Date(val);
}, z.date());
var InvoiceStatusEnum = z.enum([
  InvoiceStatus.DRAFT,
  InvoiceStatus.SENT,
  InvoiceStatus.PAID,
  InvoiceStatus.EXPIRED,
  InvoiceStatus.VOID
]);
var PaymentStatusEnum = z.enum([
  PaymentStatus.PENDING,
  PaymentStatus.SUCCEEDED,
  PaymentStatus.FAILED,
  PaymentStatus.REFUNDED
]);
var PaymentMethodEnum = z.enum([
  PaymentMethod.CARD,
  PaymentMethod.ACH,
  PaymentMethod.APPLE_PAY,
  PaymentMethod.GOOGLE_PAY,
  PaymentMethod.OTHER
]);
var InvoiceLineItemSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  description: z.string().min(1).max(1e3),
  /** Quantity of units (e.g. hours), up to 4 decimal places */
  quantity: z.number().nonnegative(),
  /**
   * Historical rate at time of invoicing — never a live lookup.
   * Matches Prisma: rateSnapshot Decimal @db.Decimal(10, 2)
   */
  rateSnapshot: z.number().nonnegative(),
  /**
   * Pre-calculated line total: quantity × rateSnapshot, denormalised for
   * immutability. Matches Prisma: amount Decimal @db.Decimal(12, 2)
   */
  amount: z.number().nonnegative(),
  createdAt: z.coerce.date()
});
var InvoiceSchema = z.object({
  id: uuidSchema,
  /** 6-digit uppercase hex string — e.g. "00001A" */
  invoiceNumber: z.string().regex(INVOICE_NUMBER_REGEX, "Invoice number must be 6-digit uppercase hex"),
  clientId: uuidSchema,
  status: InvoiceStatusEnum,
  /** Sum of all line item amounts. Matches Prisma: subtotal Decimal @db.Decimal(12, 2) */
  subtotal: z.number().nonnegative(),
  /** Invoice total. Matches Prisma: total Decimal @db.Decimal(12, 2) */
  total: z.number().nonnegative(),
  /** Non-nullable in Prisma — must be set when invoice is created */
  issuedAt: requiredDateSchema,
  /** Non-nullable in Prisma — must be set when invoice is created */
  dueAt: requiredDateSchema,
  paidAt: z.coerce.date().nullable(),
  lineItems: z.array(InvoiceLineItemSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /**
   * Sum of succeeded payments, formatted to 2 dp (e.g. "700.00").
   * Derived by the API — not stored. Optional so existing list views that
   * don't yet carry balance data stay type-safe.
   */
  amountPaid: z.string().optional(),
  /**
   * max(0, total − amountPaid), formatted to 2 dp (e.g. "2691.67").
   * Derived by the API — not stored.
   */
  balanceDue: z.string().optional()
});
var InvoiceCreateInputSchema = z.object({
  clientId: uuidSchema,
  /**
   * Number of days from today until payment is due.
   * Defaults to 30 if omitted.
   */
  dueInDays: z.number().int().positive().max(365).optional(),
  /**
   * Time entry IDs to roll up into line items.
   * If omitted, the invoice is created as an empty draft.
   */
  timeEntryIds: z.array(uuidSchema).optional()
});
var PaymentSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  /** FK to PaymentLink — set when payment originates from a link, null otherwise */
  paymentLinkId: uuidSchema.nullable(),
  /** Stripe PaymentIntent ID — nullable until a payment attempt is made */
  stripePaymentIntentId: z.string().max(255).nullable(),
  /** Stripe Checkout Session ID — nullable if using PaymentIntent flow */
  stripeCheckoutSessionId: z.string().max(255).nullable(),
  /** Amount charged. Matches Prisma: amount Decimal @db.Decimal(12, 2) */
  amount: z.number().positive(),
  status: PaymentStatusEnum,
  /**
   * Nullable — set by the webhook (#26) once the customer's actual payment
   * method is known. At checkout-session creation time the method is unknown.
   */
  method: PaymentMethodEnum.nullable(),
  paidAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var PaymentHistoryItemSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  paymentLinkId: uuidSchema.nullable(),
  /** Raw Decimal string — may lack trailing zeros. Normalize for display. */
  amount: z.string(),
  status: PaymentStatusEnum,
  method: PaymentMethodEnum.nullable(),
  paidAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  paymentLink: z.object({ id: uuidSchema }).nullable()
});
var PaymentLinkSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  /**
   * SHA-256 hash of the plaintext token.
   * The plaintext token is generated at creation time and never stored.
   * Matches Prisma: tokenHash String @unique
   */
  tokenHash: z.string().min(1).max(128),
  expiresAt: z.coerce.date(),
  /**
   * Set when the associated payment succeeds — null until then.
   * Matches Prisma: usedAt DateTime?
   */
  usedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date()
});
var PaymentLinkCreateInputSchema = z.object({
  invoiceId: uuidSchema,
  /**
   * Number of days until this link expires.
   * Defaults to DEFAULT_PAYMENT_LINK_EXPIRY_DAYS (30) if omitted.
   */
  expiresInDays: z.number().int().positive().max(365).default(DEFAULT_PAYMENT_LINK_EXPIRY_DAYS)
});
var ExternalPaymentInputSchema = z.object({
  /** Amount in dollars, e.g. 700.00 */
  amount: z.number().positive("Amount must be greater than zero"),
  /** Must be 'other' — the Prisma PaymentMethod enum value for non-Stripe receipts */
  method: z.literal("other"),
  /** ISO 8601 date/datetime string — when the payment was received */
  paidAt: z.string().datetime({ offset: true })
});
var PaymentLinkCreateResponseSchema = z.object({
  id: uuidSchema,
  /** Plaintext token — returned once on creation, never retrievable again. */
  token: z.string().min(1),
  /** Full URL the client should share with the payer, e.g. https://aelita.app/pay/<token> */
  url: z.string().url(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date()
});
var PaymentLinkStatusEnum = z.enum(["active", "expired", "used"]);
var PaymentLinkListItemSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  expiresAt: z.coerce.date(),
  usedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  /** Derived: 'used' if usedAt is set, 'expired' if expiresAt <= now, else 'active'. */
  status: PaymentLinkStatusEnum
});
var PublicInvoiceLineItemSchema = z.object({
  description: z.string(),
  /** Pre-calculated line total (matches InvoiceLineItem.amount) */
  amount: z.number().nonnegative(),
  /**
   * Quantity of hours worked. Serialised as a JS number, up to 4dp
   * (matches Prisma: quantity Decimal @db.Decimal(10, 4)).
   * Optional — absent on legacy rows that pre-date this field.
   * The API sends the stored precision (e.g. 61.6667); the frontend
   * rounds for display (e.g. "61.67").
   */
  hours: z.number().nonnegative().optional(),
  /**
   * Historical rate at time of invoicing, serialised as a JS number
   * (matches the existing `amount` convention on this schema and the
   * Prisma column: rateSnapshot Decimal @db.Decimal(10, 2)).
   * Optional — absent on legacy rows. Intentionally exposed: see comment
   * on PublicInvoiceLineItemSchema above.
   */
  rateSnapshot: z.number().nonnegative().optional()
});
var PublicInvoiceViewSchema = z.object({
  invoiceNumber: z.string().regex(INVOICE_NUMBER_REGEX),
  clientName: z.string(),
  lineItems: z.array(PublicInvoiceLineItemSchema),
  subtotal: z.number().nonnegative(),
  total: z.number().nonnegative(),
  /** Non-nullable — invoice must be issued before it can be sent to a client */
  issuedAt: requiredDateSchema,
  /** Non-nullable — due date is required for client-facing view */
  dueAt: requiredDateSchema,
  /**
   * Sum of succeeded payments, serialised as a decimal string ("700.00").
   * Allows the payer to see "Already paid $700.00" and understand why the
   * amount due differs from the invoice total.
   */
  amountPaid: z.string().regex(/^\d+\.\d{2}$/),
  /**
   * Amount the payer owes now, serialised as a decimal string ("2691.67").
   * This is exactly what Stripe will charge. "0.00" when the invoice is
   * fully settled — the public page must not offer payment in that case.
   */
  balanceDue: z.string().regex(/^\d+\.\d{2}$/)
});

// src/validation/index.ts
var uuidSchema2 = z.string().uuid();
var paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(PAGINATION.DEFAULT_PAGE),
  limit: z.coerce.number().int().positive().max(PAGINATION.MAX_LIMIT).default(PAGINATION.DEFAULT_LIMIT)
});
var dateSchema = z.coerce.date();
var optionalDateSchema = z.coerce.date().nullable().optional();
var emailSchema = z.string().email().max(255);
var optionalEmailSchema = z.preprocess(
  (val) => val === "" ? null : val,
  z.string().email().max(255).nullable().optional()
);
var passwordSchema = z.string().min(AUTH.PASSWORD_MIN_LENGTH, `Password must be at least ${AUTH.PASSWORD_MIN_LENGTH} characters`).max(AUTH.PASSWORD_MAX_LENGTH, `Password must be at most ${AUTH.PASSWORD_MAX_LENGTH} characters`);
var phoneSchema = z.preprocess(
  (val) => val === "" ? null : val,
  z.string().regex(/^[+]?[\d\s()-]+$/, "Invalid phone number format").max(50).nullable().optional()
);
var urlSchema = z.preprocess(
  (val) => val === "" ? null : val,
  z.string().url().max(500).nullable().optional()
);
var labelsSchema = z.array(z.string().max(50)).max(20).default([]);
var skillsSchema = z.array(z.string().max(100)).max(50).default([]);
var clientStatusSchema = z.enum([
  ClientStatus.PROSPECT,
  ClientStatus.ACTIVE,
  ClientStatus.INACTIVE,
  ClientStatus.CHURNED,
  ClientStatus.ARCHIVED
]);
var employeeRoleSchema = z.enum([
  EmployeeRole.ADMIN,
  EmployeeRole.MANAGER,
  EmployeeRole.DEVELOPER,
  EmployeeRole.DESIGNER,
  EmployeeRole.QA,
  EmployeeRole.DEVOPS,
  EmployeeRole.PM,
  EmployeeRole.OTHER
]);
var employeeStatusSchema = z.enum([
  EmployeeStatus.ACTIVE,
  EmployeeStatus.INACTIVE,
  EmployeeStatus.ON_LEAVE
]);
var projectStatusSchema = z.enum([
  ProjectStatus.PIPELINE,
  ProjectStatus.ACTIVE,
  ProjectStatus.ON_HOLD,
  ProjectStatus.COMPLETED,
  ProjectStatus.CANCELLED,
  ProjectStatus.ARCHIVED
]);
var projectTypeSchema = z.enum([
  ProjectType.CLIENT,
  ProjectType.INTERNAL,
  ProjectType.RND,
  ProjectType.MAINTENANCE
]);
var billingTypeSchema = z.enum([
  BillingType.FIXED,
  BillingType.HOURLY,
  BillingType.RETAINER
]);
var taskStatusSchema = z.enum([
  TaskStatus.BACKLOG,
  TaskStatus.TODO,
  TaskStatus.IN_PROGRESS,
  TaskStatus.REVIEW,
  TaskStatus.DONE,
  TaskStatus.CANCELLED
]);
var taskTypeSchema = z.enum([
  TaskType.EPIC,
  TaskType.STORY,
  TaskType.TASK,
  TaskType.BUG,
  TaskType.SUBTASK
]);
var taskPrioritySchema = z.enum([
  TaskPriority.CRITICAL,
  TaskPriority.HIGH,
  TaskPriority.MEDIUM,
  TaskPriority.LOW
]);
var milestoneStatusSchema = z.enum([
  MilestoneStatus.PENDING,
  MilestoneStatus.IN_PROGRESS,
  MilestoneStatus.COMPLETED,
  MilestoneStatus.MISSED
]);
var documentStatusSchema = z.enum([
  DocumentStatus.DRAFT,
  DocumentStatus.PUBLISHED,
  DocumentStatus.ARCHIVED
]);
var createClientSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: optionalEmailSchema,
  phone: phoneSchema,
  address: z.string().max(500).nullable().optional(),
  website: urlSchema,
  industry: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().max(100).nullable().optional()
  ),
  status: clientStatusSchema.default(ClientStatus.PROSPECT),
  notes: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().max(5e3).nullable().optional()
  ),
  contractValue: z.preprocess(
    (val) => val === "" || val === null || Number.isNaN(val) ? null : val,
    z.number().nonnegative().nullable().optional()
  ),
  contractStart: optionalDateSchema,
  contractEnd: optionalDateSchema
});
var updateClientSchema = createClientSchema.partial();
var createClientContactSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  title: z.string().max(100).nullable().optional(),
  email: emailSchema.nullable().optional(),
  phone: phoneSchema,
  isPrimary: z.boolean().default(false)
});
var updateClientContactSchema = createClientContactSchema.partial();
var createEmployeeSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema,
  avatarUrl: urlSchema,
  role: employeeRoleSchema.default(EmployeeRole.DEVELOPER),
  department: z.string().max(100).nullable().optional(),
  title: z.string().max(100).nullable().optional(),
  hourlyRate: z.number().nonnegative().nullable().optional(),
  billableRate: z.number().nonnegative().nullable().optional(),
  hireDate: optionalDateSchema,
  status: employeeStatusSchema.default(EmployeeStatus.ACTIVE),
  managerId: uuidSchema2.nullable().optional(),
  skills: skillsSchema
});
var updateEmployeeSchema = createEmployeeSchema.partial().omit({ password: true }).extend({
  password: passwordSchema.optional()
});
var createProjectSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(5e3).nullable().optional(),
  clientId: uuidSchema2.nullable().optional(),
  status: projectStatusSchema.default(ProjectStatus.PIPELINE),
  type: projectTypeSchema.default(ProjectType.CLIENT),
  billingType: billingTypeSchema.default(BillingType.HOURLY),
  budgetHours: z.number().nonnegative().nullable().optional(),
  budgetAmount: z.number().nonnegative().nullable().optional(),
  hourlyRate: z.number().nonnegative().nullable().optional(),
  startDate: optionalDateSchema,
  targetEndDate: optionalDateSchema
});
var updateProjectSchema = createProjectSchema.partial();
var createProjectAssignmentSchema = z.object({
  employeeId: uuidSchema2,
  role: z.string().max(100).nullable().optional(),
  allocatedHoursPerWeek: z.number().nonnegative().max(168).nullable().optional(),
  startDate: optionalDateSchema,
  endDate: optionalDateSchema
});
var updateProjectAssignmentSchema = createProjectAssignmentSchema.partial().omit({
  employeeId: true
});
var createMilestoneSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(2e3).nullable().optional(),
  dueDate: optionalDateSchema,
  status: milestoneStatusSchema.default(MilestoneStatus.PENDING)
});
var updateMilestoneSchema = createMilestoneSchema.partial();
var createTaskSchema = z.object({
  projectId: uuidSchema2,
  parentTaskId: uuidSchema2.nullable().optional(),
  milestoneId: uuidSchema2.nullable().optional(),
  title: z.string().min(1, "Title is required").max(500),
  description: z.string().max(1e4).nullable().optional(),
  status: taskStatusSchema.default(TaskStatus.BACKLOG),
  priority: taskPrioritySchema.default(TaskPriority.MEDIUM),
  type: taskTypeSchema.default(TaskType.TASK),
  estimatedHours: z.number().nonnegative().nullable().optional(),
  storyPoints: z.number().int().nonnegative().max(100).nullable().optional(),
  dueDate: optionalDateSchema,
  labels: labelsSchema
});
var updateTaskSchema = createTaskSchema.partial().omit({ projectId: true }).extend({
  order: z.number().int().nonnegative().optional(),
  completedAt: optionalDateSchema
});
var createTaskCommentSchema = z.object({
  content: z.string().min(1, "Content is required").max(1e4)
});
var updateTaskCommentSchema = createTaskCommentSchema;
var createTaskDependencySchema = z.object({
  dependsOnTaskId: uuidSchema2,
  type: z.enum(["blocks", "is_blocked_by"]).default("blocks")
});
var createTimeEntrySchema = z.object({
  projectId: uuidSchema2,
  taskId: uuidSchema2.nullable().optional(),
  description: z.string().max(1e3).nullable().optional(),
  startTime: dateSchema,
  endTime: optionalDateSchema,
  duration: z.number().int().positive().max(24 * 60).optional(),
  // max 24 hours in minutes
  isBillable: z.boolean().default(true)
}).refine(
  (data) => {
    if (!data.endTime && !data.duration) {
      return false;
    }
    return true;
  },
  { message: "Either endTime or duration must be provided" }
);
var updateTimeEntrySchema = z.object({
  projectId: uuidSchema2.optional(),
  taskId: uuidSchema2.nullable().optional(),
  description: z.string().max(1e3).nullable().optional(),
  startTime: dateSchema.optional(),
  endTime: optionalDateSchema,
  duration: z.number().int().positive().max(24 * 60).optional(),
  isBillable: z.boolean().optional()
});
var startTimerSchema = z.object({
  projectId: uuidSchema2,
  taskId: uuidSchema2.nullable().optional(),
  description: z.string().max(1e3).nullable().optional()
});
var approveTimeEntriesSchema = z.object({
  timeEntryIds: z.array(uuidSchema2).min(1)
});
var createDocumentSchema = z.object({
  title: z.string().min(1, "Title is required").max(255),
  content: z.string().max(5e5),
  // ~500KB of text
  folderId: uuidSchema2.nullable().optional(),
  projectId: uuidSchema2.nullable().optional(),
  clientId: uuidSchema2.nullable().optional(),
  status: documentStatusSchema.default(DocumentStatus.DRAFT),
  tags: labelsSchema
});
var updateDocumentSchema = createDocumentSchema.partial();
var createDocumentFolderSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  parentId: uuidSchema2.nullable().optional()
});
var updateDocumentFolderSchema = createDocumentFolderSchema.partial();
var createDocumentTemplateSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(1e3).nullable().optional(),
  content: z.string().max(5e5),
  category: z.string().max(50).nullable().optional()
});
var updateDocumentTemplateSchema = createDocumentTemplateSchema.partial();
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required")
});
var refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required")
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: passwordSchema
});
var adminResetPasswordSchema = z.object({
  newPassword: passwordSchema
});
var createApiKeySchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
  scopes: z.array(z.string().max(50)).max(20).default([]),
  expiresAt: optionalDateSchema
});
var createConversationSchema = z.object({
  title: z.string().max(255).optional()
});
var sendMessageSchema = z.object({
  content: z.string().min(1, "Message is required").max(1e4)
});
var clientQuerySchema = paginationSchema.extend({
  status: clientStatusSchema.optional(),
  search: z.string().max(100).optional(),
  industry: z.string().max(100).optional()
});
var employeeStatusListSchema = z.string().transform(
  (value) => value.split(",").map((part) => part.trim()).filter(Boolean)
).refine((statuses) => statuses.length > 0, {
  message: "At least one status is required"
}).refine(
  (statuses) => statuses.every((s) => employeeStatusSchema.options.includes(s)),
  {
    message: `Status must be one of: ${employeeStatusSchema.options.join(", ")}`
  }
);
var employeeQuerySchema = paginationSchema.extend({
  status: employeeStatusListSchema.optional(),
  role: employeeRoleSchema.optional(),
  department: z.string().max(100).optional(),
  search: z.string().max(100).optional()
});
var projectQuerySchema = paginationSchema.extend({
  status: projectStatusSchema.optional(),
  type: projectTypeSchema.optional(),
  clientId: uuidSchema2.optional(),
  search: z.string().max(100).optional()
});
var taskQuerySchema = paginationSchema.extend({
  projectId: uuidSchema2.optional(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  type: taskTypeSchema.optional(),
  assigneeId: uuidSchema2.optional(),
  milestoneId: uuidSchema2.optional(),
  search: z.string().max(100).optional()
});
var timeEntryQueryBooleanSchema = z.union([z.boolean(), z.literal("true"), z.literal("false")]).transform((value) => value === true || value === "true");
var timeEntryQuerySchema = paginationSchema.extend({
  employeeId: uuidSchema2.optional(),
  projectId: uuidSchema2.optional(),
  taskId: uuidSchema2.optional(),
  isBillable: timeEntryQueryBooleanSchema.optional(),
  isApproved: timeEntryQueryBooleanSchema.optional(),
  startDate: optionalDateSchema,
  endDate: optionalDateSchema
});
var documentQuerySchema = paginationSchema.extend({
  folderId: uuidSchema2.nullable().optional(),
  projectId: uuidSchema2.optional(),
  clientId: uuidSchema2.optional(),
  status: documentStatusSchema.optional(),
  search: z.string().max(100).optional(),
  tags: z.array(z.string()).optional()
});

export { API_KEY, APP_NAME, APP_VERSION, AUTH, BillingType, CLIENT_STATUS_LABELS, COMPANY, ClientStatus, DEFAULT_PAYMENT_LINK_EXPIRY_DAYS, DocumentStatus, EMPLOYEE_ROLE_LABELS, EmployeeRole, EmployeeStatus, ExternalPaymentInputSchema, INVOICE_NUMBER_MAX_VALUE, INVOICE_NUMBER_REGEX, InsightSeverity, InsightType, InvoiceCreateInputSchema, InvoiceLineItemSchema, InvoiceSchema, InvoiceStatus, InvoiceStatusEnum, MessageRole, MilestoneStatus, PAGINATION, PORTS, PROJECT_STATUS_LABELS, PaymentHistoryItemSchema, PaymentLinkCreateInputSchema, PaymentLinkCreateResponseSchema, PaymentLinkListItemSchema, PaymentLinkSchema, PaymentLinkStatusEnum, PaymentMethod, PaymentMethodEnum, PaymentSchema, PaymentStatus, PaymentStatusEnum, ProjectStatus, ProjectType, PublicInvoiceLineItemSchema, PublicInvoiceViewSchema, TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TIME_TRACKING, TaskPriority, TaskStatus, TaskType, adminResetPasswordSchema, approveTimeEntriesSchema, billingTypeSchema, calculateDuration, capitalize, centsToDisplay, changePasswordSchema, clamp, clientQuerySchema, clientStatusSchema, createApiKeySchema, createClientContactSchema, createClientSchema, createConversationSchema, createDocumentFolderSchema, createDocumentSchema, createDocumentTemplateSchema, createEmployeeSchema, createMilestoneSchema, createProjectAssignmentSchema, createProjectSchema, createTaskCommentSchema, createTaskDependencySchema, createTaskSchema, createTimeEntrySchema, dateSchema, deepClone, diffInDays, documentQuerySchema, documentStatusSchema, emailSchema, employeeQuerySchema, employeeRoleSchema, employeeStatusListSchema, employeeStatusSchema, endOfDay, endOfWeek, formatCurrency, formatDate, formatDateISO, formatDateTime, formatDuration, formatDurationDecimal, formatFullName, formatInvoiceNumber, formatPercent, getInitials, groupBy, isEmpty, isFuture, isPast, isValidEmail, isValidUrl, isValidUuid, labelsSchema, loginSchema, milestoneStatusSchema, omit, optionalDateSchema, optionalEmailSchema, paginationSchema, parseDuration, parseInvoiceNumber, passwordSchema, phoneSchema, pick, projectQuerySchema, projectStatusSchema, projectTypeSchema, refreshTokenSchema, retry, roundTo, sendMessageSchema, skillsSchema, sleep, slugify, sortBy, startOfDay, startOfWeek, startTimerSchema, taskPrioritySchema, taskQuerySchema, taskStatusSchema, taskTypeSchema, timeEntryQuerySchema, toTitleCase, truncate, uniqueBy, updateClientContactSchema, updateClientSchema, updateDocumentFolderSchema, updateDocumentSchema, updateDocumentTemplateSchema, updateEmployeeSchema, updateMilestoneSchema, updateProjectAssignmentSchema, updateProjectSchema, updateTaskCommentSchema, updateTaskSchema, updateTimeEntrySchema, urlSchema, uuidSchema2 as uuidSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map